import { useCallback } from 'react';
import { message } from 'antd';
import {
    PASTE_OFFSET_STEP,
    DUPLICATE_OFFSET_STEP,
} from '../constants/canvas';
import {
    cloneValue,
    createEdgeId,
    copyTextToClipboard,
} from '../utils/common';
import {
    getDescendantNodeIds,
    getNodeAbsolutePosition,
    sortNodesByParentDepth,
} from '../utils/graph';
import {
    createDuplicatedNodeId,
    normalizeDuplicatedNodeData,
    appendDuplicateSuffix,
} from '../utils/clipboard';
import {
    withNodeImageSequence,
} from '../utils/imageSequence';
import { stripGroupedClipboardExecution } from '../utils/template';
import { syncConnectedTargetNodes } from '../utils/validation';

/**
 * 画布复制/粘贴/副本管理
 */
export const useCanvasClipboard = ({
    nodesRef,
    edgesRef,
    nextImageNodeNumberRef,
    setNodes,
    setEdges,
    setNextImageNodeNumber,
    pushHistory,
    persistCanvasBatch,
    areNodesLocked,
    activeId,
    clipboardRef,
    pasteCountRef,
}) => {

    // 生成复制/粘贴使用的节点与连线快照
    const buildClipboardPayloadFromState = useCallback((nodeIds, sourceNodes, sourceEdges) => {
        const targetNodeIds = getDescendantNodeIds(nodeIds, sourceNodes);
        const copiedNodes = sortNodesByParentDepth(
            sourceNodes.filter((node) => targetNodeIds.has(node.id)),
        );
        const copiedEdges = sourceEdges.filter((edge) => (
            targetNodeIds.has(edge.source) && targetNodeIds.has(edge.target)
        ));

        return cloneValue({
            nodes: copiedNodes,
            edges: copiedEdges,
        });
    }, []);

    // 生成复制/粘贴使用的节点与连线快照
    const buildClipboardPayload = useCallback((nodeIds) => (
        buildClipboardPayloadFromState(nodeIds, nodesRef.current, edgesRef.current)
    ), [buildClipboardPayloadFromState, nodesRef, edgesRef]);

    // 将剪贴板中的节点克隆到当前画布
    const cloneClipboardToCanvas = useCallback((clipboard, options = {}) => {
        if (!clipboard?.nodes?.length) return false;
        const beforeNodes = options.baseNodes || nodesRef.current;
        const beforeEdges = options.baseEdges || edgesRef.current;
        const containsGroupNode = clipboard.nodes.some((node) => node?.type === 'groupNode');

        if (!options.skipHistory) {
            pushHistory(options.historySnapshot || undefined);
        }

        const nodeIdMap = new Map();
        const clipboardNodeMap = new Map(clipboard.nodes.map((node) => [node.id, node]));
        const absolutePositions = clipboard.nodes.map((node) => getNodeAbsolutePosition(node, clipboardNodeMap));
        const minX = Math.min(...absolutePositions.map((position) => position.x));
        const minY = Math.min(...absolutePositions.map((position) => position.y));

        let deltaX = 0;
        let deltaY = 0;

        if (options.positionDelta) {
            deltaX = Number(options.positionDelta.x ?? 0);
            deltaY = Number(options.positionDelta.y ?? 0);
            pasteCountRef.current = 0;
        } else if (options.flowPosition) {
            deltaX = options.flowPosition.x - minX;
            deltaY = options.flowPosition.y - minY;
            pasteCountRef.current = 0;
        } else {
            pasteCountRef.current += 1;
            deltaX = PASTE_OFFSET_STEP * pasteCountRef.current;
            deltaY = PASTE_OFFSET_STEP * pasteCountRef.current;
        }

        clipboard.nodes.forEach((node) => {
            nodeIdMap.set(node.id, createDuplicatedNodeId(node));
        });

        const edgeIdMap = new Map();
        clipboard.edges.forEach((edge) => {
            edgeIdMap.set(edge.id, createEdgeId());
        });

        let nextImageSequence = nextImageNodeNumberRef.current;
        const nextNodes = clipboard.nodes.map((node) => {
            const nextNode = cloneValue(node);
            const absolutePosition = getNodeAbsolutePosition(node, clipboardNodeMap);
            const parentIsCopied = !!(nextNode.parentId && nodeIdMap.has(nextNode.parentId));

            nextNode.id = nodeIdMap.get(node.id);
            nextNode.selected = typeof options.selectDuplicatedNodes === 'boolean'
                ? options.selectDuplicatedNodes
                : (
                    containsGroupNode
                        ? nextNode.type === 'groupNode'
                        : true
                );
            nextNode.data = normalizeDuplicatedNodeData(node.data, { nodeIdMap, edgeIdMap });
            if (nextNode.type === 'groupNode') {
                nextNode.data = {
                    ...(nextNode.data || {}),
                    label: appendDuplicateSuffix(node.data?.label || node.data?.nodeName || '', '组'),
                };
            } else if (nextNode.type === 'imageNode' || nextNode.type === 'functionNode') {
                const duplicatedTitle = appendDuplicateSuffix(
                    node.data?.imageSequenceLabel || node.data?.label || '',
                    nextNode.type === 'imageNode' ? '图片' : '节点',
                );
                nextNode.data = {
                    ...(nextNode.data || {}),
                    imageSequenceLabel: duplicatedTitle,
                    ...(nextNode.type === 'imageNode' ? { label: duplicatedTitle } : {}),
                };
            }
            if (nextNode.type !== 'groupNode') {
                nextNode.data = withNodeImageSequence({
                    ...nextNode,
                    data: nextNode.data,
                }, nextImageSequence).data;
                nextImageSequence += 1;
            }

            if (parentIsCopied) {
                nextNode.parentId = nodeIdMap.get(nextNode.parentId);
            } else {
                delete nextNode.parentId;
                delete nextNode.extent;
                delete nextNode.expandParent;
                nextNode.position = {
                    x: absolutePosition.x + deltaX,
                    y: absolutePosition.y + deltaY,
                };
            }

            return nextNode;
        });

        const nextEdges = clipboard.edges.map((edge) => ({
            ...cloneValue(edge),
            id: edgeIdMap.get(edge.id),
            source: nodeIdMap.get(edge.source),
            target: nodeIdMap.get(edge.target),
            selected: false,
        }));

        const mergedNodes = beforeNodes.map((node) => ({ ...node, selected: false })).concat(nextNodes);
        const mergedEdges = beforeEdges.map((edge) => ({ ...edge, selected: false })).concat(nextEdges);
        const syncedMergedNodes = syncConnectedTargetNodes(mergedNodes, mergedEdges);

        setNodes(syncedMergedNodes);
        setEdges(mergedEdges);
        if (!options.skipNextImageSequenceUpdate) {
            setNextImageNodeNumber(nextImageSequence);
        }
        if (!options.skipPersist) {
            persistCanvasBatch({
                beforeNodes,
                beforeEdges,
                afterNodes: syncedMergedNodes,
                afterEdges: mergedEdges,
                projectId: options.projectId,
            });
        }

        return true;
    }, [persistCanvasBatch, pushHistory, setEdges, setNodes, nodesRef, edgesRef, nextImageNodeNumberRef, setNextImageNodeNumber, pasteCountRef]);

    // 复制当前选中的节点集合
    const handleCopy = useCallback(() => {
        const currentNodes = nodesRef.current;
        const copiedNodes = currentNodes.filter((node) => node.selected);
        if (copiedNodes.length === 0) return;

        clipboardRef.current = stripGroupedClipboardExecution(
            buildClipboardPayload(copiedNodes.map((node) => node.id)),
        );
        pasteCountRef.current = 0;
    }, [buildClipboardPayload, clipboardRef, nodesRef, pasteCountRef]);

    // 将剪贴板内容粘贴到画布
    const handlePaste = useCallback((flowPosition = null) => {
        cloneClipboardToCanvas(clipboardRef.current, { flowPosition });
    }, [clipboardRef, cloneClipboardToCanvas]);

    // 复制单个节点到剪贴板
    const handleCopyNode = useCallback((nodeId) => {
        clipboardRef.current = stripGroupedClipboardExecution(
            buildClipboardPayload([nodeId]),
        );
        pasteCountRef.current = 0;
    }, [buildClipboardPayload, clipboardRef, pasteCountRef]);

    // 基于单个节点快速创建副本
    const handleDuplicateNode = useCallback((nodeId) => {
        const sourceNode = nodesRef.current.find((node) => node.id === nodeId);
        if (!sourceNode) return;

        const clipboard = stripGroupedClipboardExecution(
            buildClipboardPayload([nodeId]),
        );

        cloneClipboardToCanvas(clipboard, {
            flowPosition: {
                x: sourceNode.position.x + DUPLICATE_OFFSET_STEP,
                y: sourceNode.position.y + DUPLICATE_OFFSET_STEP,
            },
        });
    }, [buildClipboardPayload, cloneClipboardToCanvas, nodesRef]);

    // 基于选中节点批量创建副本
    const handleDuplicateSelection = useCallback((nodeIds) => {
        const normalizedNodeIds = Array.from(new Set((nodeIds || []).filter(Boolean)));
        if (normalizedNodeIds.length === 0 || areNodesLocked(normalizedNodeIds)) return;

        const sourceNodes = nodesRef.current.filter((node) => normalizedNodeIds.includes(node.id));
        if (!sourceNodes.length) return;

        const clipboard = buildClipboardPayload(normalizedNodeIds);
        const minX = Math.min(...sourceNodes.map((node) => node.position.x));
        const minY = Math.min(...sourceNodes.map((node) => node.position.y));

        cloneClipboardToCanvas(clipboard, {
            flowPosition: {
                x: minX + DUPLICATE_OFFSET_STEP,
                y: minY + DUPLICATE_OFFSET_STEP,
            },
        });
    }, [areNodesLocked, buildClipboardPayload, cloneClipboardToCanvas, nodesRef]);

    // 解析当前拖拽场景下应该复制哪些顶层节点
    const getDragDuplicateSourceNodeIds = useCallback((draggedNodeId) => {
        const currentNodes = nodesRef.current;
        const draggedNode = currentNodes.find((node) => node.id === draggedNodeId);
        if (!draggedNode) return [];

        const selectedTopLevelNodeIds = currentNodes
            .filter((node) => node.selected && !node.parentId)
            .map((node) => node.id);

        if (draggedNode.selected && selectedTopLevelNodeIds.length > 0) {
            return selectedTopLevelNodeIds;
        }

        return [draggedNodeId];
    }, [nodesRef]);

    // 复制节点调试所需的任务信息
    const handleCopyTaskId = useCallback(async (nodeId) => {
        const targetNode = nodesRef.current.find((node) => node.id === nodeId);
        if (!targetNode) {
            message.error('未找到当前节点');
            return;
        }

        const executionId = targetNode.data?.latestExecution?.executionId
            || targetNode.latestExecution?.executionId
            || '';
        const taskText = [
            `projectId: ${activeId || ''}`,
            `nodeId: ${targetNode.id || ''}`,
            `executionId: ${executionId}`,
        ].join('\n');

        try {
            const copied = await copyTextToClipboard(taskText);
            if (!copied) {
                message.error('复制失败');
                return;
            }
 
            message.success('复制成功！');
        } catch (error) {
            message.error('复制失败');
        }
    }, [activeId, nodesRef]);

    return {
        clipboardRef,
        pasteCountRef,
        buildClipboardPayloadFromState,
        buildClipboardPayload,
        cloneClipboardToCanvas,
        handleCopy,
        handlePaste,
        handleCopyNode,
        handleDuplicateNode,
        handleDuplicateSelection,
        getDragDuplicateSourceNodeIds,
        handleCopyTaskId,
    };
};